import { List, ListItem } from "@shared/components/Data/List";
import { Text } from "@solomon-labs/design/components/ui";
import React from "react";

interface PerkRowProps {
    label: string;
    multiplier: string;
    footnote?: boolean;
}

function PerkRow({
    label,
    multiplier,
    footnote
}: PerkRowProps): React.ReactElement {
    return (
        <ListItem
            itemKey={label}
            trailing={
                <Text
                    variant="number"
                    className="text-foreground-base relative z-10"
                >
                    {multiplier}
                </Text>
            }
        >
            <Text
                variant="body"
                render={<span />}
                className="text-foreground-base flex-1 relative z-10"
            >
                {label}
                {footnote && (
                    <sup className="text-[9px] text-foreground-imprint">1</sup>
                )}
            </Text>
        </ListItem>
    );
}

export const PERKS = [
    { label: "Hold 15k SOLO", multiplier: "1.15x", footnote: true },
    { label: "Hold USDv", multiplier: "1.5x" },
    { label: "Earn with USDv", multiplier: "1.0x" },
    { label: "YaaS", multiplier: "2x" },
    { label: "Loop USDv 3x", multiplier: "2x" },
    { label: "Meteora MET-USDv Pool", multiplier: "2x" },
    { label: "Hold SMB Gen2", multiplier: "2.5x" }
];

interface PerksListProps {
    /**
     * Page layout width wrapper. The landing page pins the list to a
     * fixed-width column; the positions page lets the list stretch.
     */
    width?: string;
    /** Show the inline footnote under the list (positions variant). */
    showFootnote?: boolean;
}

/**
 * @version 1.1.0
 * @published 2026-04-23
 * @history
 *   1.1.0 — Dropped per-row icons. Five of the seven icons referenced files
 *           under a `Prototype/assets/icons/` folder that never landed in
 *           shared; the two that resolved were dropped for visual
 *           consistency rather than leaving a mixed list.
 *   1.0.0 — Initial publish from prototype/pages/Perks/PerksPage.tsx and
 *           PerksPositionsPage.tsx. Single Component that covers both the
 *           landing page's fixed-width list and the positions page's wider
 *           list-with-footnote variant. Uses `ListItem` (inside `List`) for each entry.
 */
export function PerksList({
    width,
    showFootnote
}: PerksListProps = {}): React.ReactElement {
    return (
        <div className={`flex flex-col gap-3 ${width ?? ""}`}>
            <Text variant="body" className="text-foreground-neutral">
                Ways to earn points
            </Text>
            <List className="flex flex-col">
                <div className="border-t border-line-neutral" />
                {PERKS.map((perk) => (
                    <PerkRow
                        key={perk.label}
                        label={perk.label}
                        multiplier={perk.multiplier}
                        footnote={perk.footnote}
                    />
                ))}
            </List>
            {showFootnote && (
                <Text
                    variant="fine-print"
                    className="text-foreground-imprint mt-1"
                >
                    <sup className="text-[8px]">1</sup> Global boost, applies on
                    top of all points you earn.
                </Text>
            )}
        </div>
    );
}
